import { Injectable } from '@angular/core';
import { GamutPaletteService } from './gamut-palette.service';

@Injectable({
  providedIn: 'root'
})
export class GamutBrandingService {
  brandList!: any;

  constructor(private gamutPaletteService: GamutPaletteService) {}

  /**
   * @function createBrandedStyleVariables
   * @memberof GamutBrandingService
   * @summary Takes a list of branding style objects and creates them as a string
   * @example
   * var brandMap = [
   *  {key: "--var-one", value: "0px"},
   *  {key: "--var-two": value: 0}
   *  {key: "--var-three": value: 1.5}
   * ];
   * var brandStyle = gamutBrandingService.createBrandedStyleVariables(brandMap);
   * //Brand style result
   * --var-one: 0px;
   * --var-two: 0;
   * --var-three: 1.5;
   * @param {type array<object>} brandVariableMap Array of branding style key/value objects, supports strings, integers and float values.
   * @returns {string} Style declarations as a string if branding variable map has variable objects, otherwise and empty string
   * @description This function produces the stylesheet declarations for the partner branding to be injected onto a page using GamutBrandingComponent()
   */
  createBrandedStyleVariables(brandVariableMap: Array<any>) {
    let styleVariables = ``;
    for (let i = 0; i < brandVariableMap.length; i++) {
      if (typeof brandVariableMap[i].key !== 'undefined' && typeof brandVariableMap[i].value !== 'undefined') {
        styleVariables += `
  ${brandVariableMap[i].key.toString()}: ${brandVariableMap[i].value.toString()};`;
      }
    }

    return styleVariables;
  }
  /**
   * @function mergeStyleVariables
   * @memberof GamutBrandingService
   * @summary Merges two arrays of objets into a single array
   * @example
   * var colourStyles = [
   *  {key: "--var-color-default", value: "green"},
   *  {key: "--var-color-hovered", value: "orange"},
   *  {key: "--var-color-pressed", value: "yellow"},
   *  {key: "--var-color-disabled", value: "red"}
   * ];
   * var shapeStyles = [
   *  {key: "--var-border-default", value: "0px"},
   *  {key: "--var-border-hovered", value: "1px"},
   *  {key: "--var-border-selected", value: "2px"}
   * ];
   * var mergedStyleMap = gamutBrandingService.mergeStyleVariables(colourStyles, shapeStyles);
   * //Outputs result
   * [
   *  {key: "--var-color-default", value: "green"},
   *  {key: "--var-color-hovered", value: "orange"},
   *  {key: "--var-color-pressed", value: "yellow"},
   *  {key: "--var-color-disabled", value: "red"},
   *  {key: "--var-border-default", value: "0px"},
   *  {key: "--var-border-hovered", value: "1px"},
   *  {key: "--var-border-selected", value: "2px"}
   * ]
   * @param {type any} paletteStyles array of palette style variable objects for prmary and secondary colors
   * @param {type any} brandStyles array of branding style variable objects from partner branding api request
   * @returns {array} Merged array of style variable objects
   * @description This function is a utility method to reconcile the old partner branding with the new approaches being used in partner branding v2
   * @todo This function will deprecate with the switch to partner branding v2, may want to keep this as a pure utility function
   */
  mergeStyleVariables(paletteStyles: any, brandStyles: any) {
    const mergeStyles = [];
    for (let i = 0, j = paletteStyles.length; i < j; i++) {
      if (typeof paletteStyles[i].key !== 'undefined' && typeof paletteStyles[i].value !== 'undefined') {
        const paletteStyle = {key: paletteStyles[i].key, value: paletteStyles[i].value};
        mergeStyles.push(paletteStyle);
      }
    }
    for (let i = 0, j = brandStyles.length; i < j; i++) {
      if (typeof brandStyles[i].key !== 'undefined' && typeof brandStyles[i].value !== 'undefined') {
        const brandStyle = {key: brandStyles[i].key, value: brandStyles[i].value};
        mergeStyles.push(brandStyle);
      }
    }

    return mergeStyles;
  }
  /**
   * @function updateStyleVariableMap
   * @memberof GamutBrandingService
   * @summary Adds a style value to a branding variable map
   * @example
   * var map: any[] = [{key: "foo", value: "bar"}];
   * var key = "foo";
   * var value = "baz";
   * map = service.updateStyleVariableMap(map, key, value);
   * //Returns [{key: "foo", value: "baz"}]
   * @param {any[]} map List of objects with the properties: key, value
   * @param {string} key Style property key used to generate css variables
   * @param {string} value Value of the style property being mapped
   * @returns {any[]} The map param with value updated or built and push()ed
   * @description The style variable map is used to build the css variables for partner branding.
   * This method is used to construct and modify this data object
   */
  updateStyleVariableMap(map: any[], key: string, value: string) {
    let hasValue = false;
    for (let i = 0, j = map.length; i < j; i++) {
      if (map[i].key === key) {
        hasValue = true;
        map[i].value = value;
      }
    }

    if (!hasValue) {
      const newValue = {key: key, value: value};
      map.push(newValue);
    }

    return map;
  }
  /**
   * @function getStyleMapValue
   * @memberof GamutBrandingService
   * @summary Gets a style map value with default of empty string
   * @example
   * var brandStyles = [
   *  {key: "--theme-primary-color", value: "red"},
   *  {key: "--theme-secondary-color": value: "blue"}
   *  {key: "--theme-secondary-color": value: "blue"}
   *  {key: "--theme-hyperlink-color": value: "red"}
   *  {key: "--theme-secondary-svg-color": value: "blue"}
   * ];
   * var primaryColour = getStyleMapValue(brandStyles, "--theme-primary-color");
   * // return value = "red"
   * @param {any[]} map List of style variables objects with the properties: key, value
   * @param {string} key The property key to find on the style variable map
   * @returns {string} Style value if it is set on map, otherwise an empty string
   * @description This function can be used to both initialise a style map and get a value from it.
   */
  getStyleMapValue(map: any[], key: string) {
    let styleValue = '';
    for (let i = 0, j = map.length; i < j; i++) {
      if (map[i].key === key) {
        styleValue = map[i].value;
      }
    }

    return styleValue;
  }
  /**
   * @function convertTokenToField
   * @memberof GamutBrandingService
   * @summary Converts hyphen separated token names to database camelcase field labels
   * @example
   * var token = "action-primary-disabled";
   * var field = gamutBrandingService.convertTokenToField(token); // returns "actionPrimaryDisabled"
   * @param {string} token Partner branding token to change into a database field
   * @returns {string} Converted value if token is formatted as a brand token, otherwise the supplied value
   * @description This is an internal use function to reconcille how the values come out of the database and feed into mapBrandedStyleVariables().
   * This function is used in a helper capacity inside of partner test applets such as the brand form
   */
  convertTokenToField(token: string): string {
    const words = token.split('-');
    let field = words[0];
    for (let i = 1, j = words.length; i < j; i++) {
      field = field + words[i].charAt(0).toUpperCase() + words[i].slice(1);
    }

    return field;
  }
  /**
   * @function isUnits
   * @memberof GamutBrandingService
   * @summary Validates if a branding value is a non-relative CSS unit set
   * @example
   * var test1 = gamutBrandingService.isUnits("0"); // returns true
   * var test2 = gamutBrandingService.isUnits("16 24"); // returns true
   * var test3 = gamutBrandingService.isUnits("16 24 8"); // returns true
   * var test4 = gamutBrandingService.isUnits("16 24 8 32"); // returns true
   * var test5 = gamutBrandingService.isUnits("16 24 8 32 24"); // returns false
   * var test6 = gamutBrandingService.isUnits("16px 24px 8px 32px"); // returns true
   * var test7 = gamutBrandingService.isUnits("16px 24 8 32px"); // returns true
   * var test8 = gamutBrandingService.isUnits("1em"); // returns false cannot use relative units
   * var test9 = gamutBrandingService.isUnits("px"); // returns false
   * @param {string} units Branding value to test if it is units
   * @returns {boolean} TRUE if units are pixel or pure numeric values, other FALSE
   * @description This function guards against the use of em, rem, %, and other relative units that will pollute the design system
   */
  isUnits(units: string): boolean {
    const unitList = units.split(' ');
    if (unitList.length > 4) {
      return false;
    }
    const unitRegex = /^[0-9]+(px)*/g;
    for (let i = 0, j = unitList.length; i < j; i++) {
      const match = unitList[i].match(unitRegex);
      if (match === null || match.length !== 1 || match[0] !== unitList[i]) {
        return false;
      }
    }
    return true;
  }
  /**
   * @function isSize
   * @memberof GamutBrandingService
   * @summary Validates if a branding value is a named size
   * @example
   * var test1 = gamutBrandingService.isSize('small'); // returns true
   * var test2 = gamutBrandingService.isSize("xsmall"); // returns true
   * var test3 = gamutBrandingService.isSize("medium"); // returns true
   * var test4 = gamutBrandingService.isSize('large'); // returns true
   * var test5 = gamutBrandingService.isSize("xlarge"); // returns true
   * var test6 = gamutBrandingService.isSize('none'); // returns true
   * var test7 = gamutBrandingService.isSize(""); // returns false
   * var test8 = gamutBrandingService.isSize("foo"); // returns false
   * @param {string} size descriptive size of partner branding value
   * @returns {boolean} TRUE if size is a valid named value, otherwise FALSE
   * @description Size is an internal value(xsmall, small, medium, large, xlarge, none) converted to a predefined CSS value
   */
  isSize(size: string): boolean {
    if (size === 'xsmall' || size === 'small' || size === 'medium' || size === 'large' || size === 'xlarge' || size === 'none') {
      return true;
    }
    return false;
  }
  /**
   * @function isUrl
   * @memberof GamutBrandingService
   * @summary Validates if a branding value is a url string
   * @example
   * var test1 = gamutBrandingService.isUrl("https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800;900&display=swap"); //returns true
   * var test2 = gamutBrandingService.isUrl("fonts/title.css"); //returns false
   * @param {string} url Web resource used within the partner branding system
   * @returns {boolean} TRUE if url is correctly formatted, otherwise FALSE
   * @description Urls are currently used for fonts. Validation is performed in isValid() but can be broken out for specific validation scenarios
   */
  isUrl(url: string): boolean {
    const urlRegex = /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/g;
    const match = url.match(urlRegex);
    if (match === null || match.length !== 1 || match[0] !== url) {
      return false;
    }
    return true;
  }
  /**
   * @function isFont
   * @memberof GamutBrandingService
   * @summary Validates if a branding value is a font name
   * @example
   * var test1 = gamutBrandingService.isFont("Open Sans"); // returns true
   * var test2 = gamutBrandingService.isFont("untitled-sans"); // returns true
   * var test3 = gamutBrandingService.isFont("'Open Sans'"); // returns true
   * var test4 = gamutBrandingService.isFont("'untitled-sans'"); // returns true
   * var test5 = gamutBrandingService.isFont('"Open Sans"'); // returns true
   * var test6 = gamutBrandingService.isFont('"untitled-sans"'); // returns true
   * var test7 = gamutBrandingService.isFont("https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700;800;900&display=swap"); //returns false
   * @param {string} font Name of a font used in the css declaration font-family
   * @returns {boolen} TRUE if font is a valid name format, otherwise FALSE
   * @description Validation only makes guesses on a single space, underscore or hyphen in name wrapped in single quotations
   */
  isFont(font: string): boolean {
    const fontRegex = /^((\'|\")*([a-z-A-Z0-9\-])+(( ){1})*(\'|\")*)*/g;
    const match = font.match(fontRegex);
    if (match === null || match.length !== 1 || match[0] !== font) {
      return false;
    }
    return true;
  }
  /**
   * @function isImage
   * @memberof GamutBrandingService
   * @summary Validates if and image is a file path to an image asset
   * @example
   * var test1 = gamutBrandingService.isImage("images/logo.png"); // returns true
   * var test2 = gamutBrandingService.isImage("partner-images/logo_small.png"); // returns true
   * var test3 = gamutBrandingService.isImage("IMAGES/LOGO.PNG"); // returns true
   * var test4 = gamutBrandingService.isImage("PARTNEY-IMAGES/LOGO_SMALL.PNG"); // returns true
   * var test5 = gamutBrandingService.isImage("https://media.beopen.com/images/logo.png"); // returns false
   * @param {string} image Relative file path to an image asset
   * @returns {boolen} TRUE if image is a clean relative file path, otherwise FALSE
   * @description Image paths and file names cannot contain a space
   */
  isImage(image: string): boolean {
    const imageRegex = /^([a-z-A-Z0-9\/\_\-]*)+.(jpg|JPG|jpeg|JPEG|png|PNG|gif|GIF|ico|ICO)+/g;
    const match = image.match(imageRegex);
    if (match === null || match.length !== 1 || match[0] !== image) {
      return false;
    }
    return true;
  }
  /**
   * @function isColor
   * @memberof GamutBrandingService
   * @summary Extension of the GamutPaletteService isValid method, forcing a string type
   * @example
   * var test1 = this.gamutBrandingService.isColor("green"); // returns true
   * var test2 = this.gamutBrandingService.isColor("#00FF00"); // returns true
   * var test3 = this.gamutBrandingService.isColor("#00ff00"); // returns true
   * var test4 = this.gamutBrandingService.isColor("#0F0"); // returns true
   * var test5 = this.gamutBrandingService.isColor("0F0"); // returns true
   * var test6 = this.gamutBrandingService.isColor("foo"); // returns false
   * var test7 = this.gamutBrandingService.isColor("#00FF0"); // returns false
   * @param {string} value CSS colour value as a string
   * @returns {boolean} TRUE if color is a valid colour string, otherwise FALSE
   * @description This function is used with a web UI(EG; BrandFormComponent) and can be developed out to do more specific validation
   */
   isColor(color: string): boolean {
    if (this.gamutPaletteService.isValid(color)) {
      return true;
    }

    return false;
  }
  /**
   * @function isValid
   * @memberof GamutBrandingService
   * @summary Extension of the GamutPaletteService isValid method, forcing a string type
   * @example
   * validColour1 = this.gamutBrandingService.isValid("green");
   * validColour2 = this.gamutBrandingService.isValid("#00FF00");
   * validColour3 = this.gamutBrandingService.isValid("#00ff00");
   * validColour4 = this.gamutBrandingService.isValid("#0F0");
   * validColour5 = this.gamutBrandingService.isValid("0F0");
   * // all return true
   * @param {string} value CSS value as a string
   * @returns {boolean} TRUE if color is a valid colour string, otherwise FALSE
   * @description This function is used with a web UI(EG; BrandFormComponent) and can be developed out to do more specific validation
   * @todo This function needs to valide shape values, always returning true for now
   */
  isValid(value: string): boolean {
    if (this.gamutPaletteService.isValid(value)) {
      return true;
    }
    else if (this.isUnits(value) || this.isSize(value) || this.isUrl(value) || this.isFont(value) || this.isImage(value)) {
      return true;
    }
    // Needs to validate shape here
    return false;
  }
  /**
   * @function getBoxShadow
   * @memberof GamutBrandingService
   * @summary Takes a shorthand value for design system box shadows and produces the matching CSS declaration
   * @example
   * var mediumBoxShadow = gamutBrandingService.getBoxShadow("medium");
   * @param {string} size Droshadow size: 'none', "", 'small', 'large', "medium"(default)
   * @returns {string} CSS declaration for named design system box-shadow property
   * @description This function will always default to medium box shadow if no other valid value is defined. If empty value is given for size then 'none' is used
   * surface-section-shadow can only be medium and will force a small box-shadow in medium down resolution rule in the component open-raptor-section-default
   */
  getBoxShadow(size: string): string {
    let boxShadow: string = '';
    switch(size) {
      case 'none':
      case '':
        boxShadow = 'none';
        break;
      case 'small':
        boxShadow = '0px 6px 7px rgba(141, 148, 165, 0.07), 0px 3px 4px rgba(141, 148, 165, 0.07), 0px -2px 4px rgba(141, 148, 165, 0.07), 0px 2px 3px rgba(141, 148, 165, 0.07)';
        break;
      case 'large':
        boxShadow = '0px 25px 30px rgba(141, 148, 165, 0.03), 0px 15px 20px rgba(141, 148, 165, 0.06), 0px -6px 20px rgba(141, 148, 165, 0.05), 0px -4px 10px rgba(141, 148, 165, 0.07), 0px 10px 12px rgba(141, 148, 165, 0.08)';
        break;
      default:
        boxShadow = '0px 15px 20px rgba(141, 148, 165, 0.05), 0px 8px 10px rgba(141, 148, 165, 0.05), 0px -5px 12px rgba(141, 148, 165, 0.05), 0px -3px 6px rgba(141, 148, 165, 0.06), 0px 5px 5px rgba(141, 148, 165, 0.07)';
    }

    return boxShadow;
  }
  /**
   * @function getTileBorderWidth
   * @memberof GamutBrandingService
   * @summary Converts a border width style value into an integer
   * @example
   * var width1 = service.getTileBorderWidth("1px"); // returns 1
   * var width2 = service.getTileBorderWidth("1.75px"); // returns 1
   * var width3 = service.getTileBorderWidth("10px"); // returns 1
   * var width4 = service.getTileBorderWidth("1px 2px 3px 4px"); // returns 1
   * var width5 = service.getTileBorderWidth("9px"); // returns 9
   * @param {string} width Style string in the format of #px, EG; 3px
   * @returns {number} Border width as an integer. Defaults to 1 if width is not a valid format or greater than 9
   * @description This is a utility method to allow a calculation to be made on hardcoded spacing values
   * @todo To be complete this method needs a regex to properly validate width before outputting it as a value, should also be able to use number
   */
  getTileBorderWidth(width: string): number {
    const borderStyle = width.repeat(1);
    const borderWidth = parseInt(borderStyle.replace("px", "").trim());

    if (isNaN(borderWidth) || borderWidth > 9) {
      return 1;
    }
    return borderWidth;
  }
  /**
   * @function getTileBorderMap
   * @memberof GamutBrandingService
   * @summary Special variable layout rules for tiles
   * @example
   * // contextual usage
   * styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-tile-border-selected-width', partnerBrandData["tile-border-selected-width"]);
   * var tileBorderSelectedWidth = this.getTileBorderWidth(partnerBrandData["tile-border-selected-width"]);
   * var tileBorderSelectedMap = this.getTileBorderMap('selected', tileBorderSelectedWidth);
   * styleVariables = this.mergeStyleVariables(styleVariables, tileBorderSelectedMap);
   * // exmaple output of basic usage
   * var borderMap = gamutBrandingService.getTileBorderMap("", 3);
   * // outputs
   * [
   *  {key: "--theme-tile-padding", value: "22px"},
   *  {key: "--theme-tile-padding-small", value: "6px"},
   *  {key: "--theme-tile-input", value: "22px"},
   *  {key: "--theme-input-mobile", value: "18px"},
   *  {key: "--theme-tile-input-small", value: "14px"}
   * ];
   * @param {string} borderType Border variable type name: ""(default), 'selected', "hovered"
   * @param {number} borderWidth Width of border as a number using this.getTileBorderWidth(), its used value will be rounded or set to 1 if outside range or 0-9
   * @returns {any[]} Colour theme map section for additional tile layout variables
   * @description This function creates additional CSS theme variables to suport border widths up to 9px wide
   */
  getTileBorderMap(borderType: string, borderWidth: number): any[] {
    let spacingMap: any[] = [];

    const borderValue = (borderWidth > 9)? 1: Math.floor(borderWidth);
    let borderKey = '';
    if (borderType === 'hovered') {
      borderKey = '-hovered';
    }
    else if (borderType === 'selected') {
      borderKey = '-selected';
    }

    // tile spacing rules
    const padding = 25 - borderValue;
    const paddingSmall = 9 - borderValue;
    const input = 25 - borderValue;
    const inputMobile = 21 - borderValue;
    const inputSmall = 17 - borderValue;

    spacingMap = [
      {key: `--theme-tile${  borderKey  }-padding`, value: `${padding  }px`},
      {key: `--theme-tile${  borderKey  }-padding-small`, value: `${paddingSmall  }px`},
      {key: `--theme-tile${  borderKey  }-input`, value: `${input  }px`},
      {key: `--theme${  borderKey  }-input-mobile`, value: `${inputMobile  }px`},
      {key: `--theme-tile${  borderKey  }-input-small`, value: `${inputSmall  }px`}
    ];

    return spacingMap;
  }
  /**
   * @function cleanBrandStyleVariablesMap
   * @memberof GamutBrandingService
   * @summary Removes empty values from a theme colour map
   * @example
   * var map: any[] = [
   *  {key: "--var-one", value: "foo"},
   *  {key: "--var-two", value: "bar"},
   *  {key: "--var-three", value: ""}
   * ];
   * var cleanStyleMap = service.cleanBrandStyleVariablesMap(map);
   * //returns
   * [
   *  {key: "--var-one", value: "foo"},
   *  {key: "--var-two", value: "bar"}
   * ];
   * @param {any[]} map Theme variable map to clean
   * @returns {any[]} Theme variable map with empty values removed
   * @description This function is used to optimise the style variables that will be added into a brand
   */
  cleanBrandStyleVariablesMap(map: any[]): any[] {
    const cleanMap: any[] = [];
    for (let i = 0, j = map.length; i < j; i++) {
      if (map[i].value !== '') {
        cleanMap.push(map[i]);
      }
    }

    return cleanMap;
  }
  /**
   * @function buildThemeColorMap
   * @memberof GamutBrandingService
   * @summary Builds a default theme colour map from primary, secondary and tertiary color values
   * @example
   * var styleVariables: any[] = [];
   * var primaryColour = "red";
   * var secondaryColour = "green";
   * var tertiaryColour = "blue";
   * styleVariables = this.buildThemeColorMap(styleVariables, deprecatedPrimaryColour, deprecatedSecondaryColour, deprecatedTertiaryColour);
   * @param {any[]} styleVariables Colour map variable to populate generated theme into
   * @param {string} primaryColor Optional valid tinycolor string value
   * @param {string} secondaryColor Optional valid tinycolor string value
   * @param {string} tertiaryColor Optional valid tinycolor string value
   * @returns {any[]} Colour map with default values theme build from the supplied colour values
   * @description Theme building requires at least the primaryColor param to be a valid string
   */
  buildThemeColorMap(styleVariables: any[], primaryColor?: string, secondaryColor?: string, tertiaryColor?: string): any[] {
    if (typeof primaryColor !== 'undefined' && primaryColor !== '') {
      const primaryPalette = this.gamutPaletteService.buildPalette(primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary', primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-disabled', this.gamutPaletteService.getPaletteColor(primaryPalette, '100'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-hovered', this.gamutPaletteService.getPaletteColor(primaryPalette, '700'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-pressed', this.gamutPaletteService.getPaletteColor(primaryPalette, '900'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-text', '#ffffff');

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary', primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-disabled', this.gamutPaletteService.getPaletteColor(primaryPalette, '100'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-hovered', this.gamutPaletteService.getPaletteColor(primaryPalette, '700'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-pressed', this.gamutPaletteService.getPaletteColor(primaryPalette, '900'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-text', primaryColor);

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary', primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-disabled', this.gamutPaletteService.getPaletteColor(primaryPalette, '100'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-hovered', this.gamutPaletteService.getPaletteColor(primaryPalette, '700'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-pressed', this.gamutPaletteService.getPaletteColor(primaryPalette, '900'));

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-on-dark', primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-disabled-on-dark', this.gamutPaletteService.getPaletteColor(primaryPalette, '100'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-hovered-on-dark', this.gamutPaletteService.getPaletteColor(primaryPalette, '700'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-pressed-on-dark', this.gamutPaletteService.getPaletteColor(primaryPalette, '900'));

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-activated', primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-hovered', this.gamutPaletteService.getPaletteColor(primaryPalette, '700'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-disabled', this.gamutPaletteService.getPaletteColor(primaryPalette, '100'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-activated-background', this.gamutPaletteService.getPaletteColor(primaryPalette, '50'));

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative', primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-two', primaryColor);
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-icon', '#ffffff');
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-text', '#ffffff');

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight', this.gamutPaletteService.getPaletteColor(primaryPalette, '50'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-text', this.gamutPaletteService.getPaletteColor(primaryPalette, '900'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured', this.gamutPaletteService.getPaletteColor(primaryPalette, '50'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured-text', this.gamutPaletteService.getPaletteColor(primaryPalette, '500'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-section', '#ffffff');

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-section', '#d5d8de');
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-section-featured', this.gamutPaletteService.getPaletteColor(primaryPalette, '200'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-surface-highlight', this.gamutPaletteService.getPaletteColor(primaryPalette, '200'));

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-icon', this.gamutPaletteService.getPaletteColor(primaryPalette, '400'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-icon-disabled', this.gamutPaletteService.getPaletteColor(primaryPalette, '100'));
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-text', primaryColor);

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-background', '#f8f9fb');

      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-header-background-colour', '#ffffff');
      styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-footer-background-colour', '#ffffff');

      if (typeof secondaryColor !== 'undefined' && secondaryColor !== '') {
        const secondaryPalette = this.gamutPaletteService.buildPalette(secondaryColor);
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary', secondaryColor);
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-disabled', this.gamutPaletteService.getPaletteColor(secondaryPalette, '100'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-hovered', this.gamutPaletteService.getPaletteColor(secondaryPalette, '700'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-pressed', this.gamutPaletteService.getPaletteColor(secondaryPalette, '900'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-text', secondaryColor);

        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-two', secondaryColor);
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-text', this.gamutPaletteService.getPaletteColor(secondaryPalette, '900'));

        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-surface-highlight', this.gamutPaletteService.getPaletteColor(secondaryPalette, '200'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight', this.gamutPaletteService.getPaletteColor(secondaryPalette, '50'));
      }

      if (typeof tertiaryColor !== 'undefined' && tertiaryColor !== '') {
        const tertiaryPalette = this.gamutPaletteService.buildPalette(tertiaryColor);
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary', tertiaryColor);
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-disabled', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '100'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-hovered', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '700'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-pressed', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '900'));

        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-on-dark', tertiaryColor);
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-disabled-on-dark', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '100'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-hovered-on-dark', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '700'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-pressed-on-dark', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '900'));

        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative', tertiaryColor);
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '50'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured-text', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '500'));

        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-section-featured', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '200'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-icon', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '400'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-icon-disabled', this.gamutPaletteService.getPaletteColor(tertiaryPalette, '100'));
        styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-text', tertiaryColor);
      }
    }

    return styleVariables;
  }
  /**
   * @function mapBrandedStyleVariables
   * @memberof GamutBrandingService
   * @summary Produces the stylesheet variables for the partner branding v2, deprecated support for branding v1
   * @example
   * //Branding v1
   * var partnerBrand = {
   *  primaryColour: "red",
   *  secondaryColour: "blue"
   * };
   * var brandMap = gamutBrandingService.mapBrandedStyleVariables(partnerBrand);
   * // result value
   * var brandMap = [
   *  {key: "--theme-primary-color", value: "red"},
   *  {key: "--theme-secondary-color": value: "blue"}
   *  {key: "--theme-secondary-color": value: "blue"}
   *  {key: "--theme-hyperlink-color": value: "red"}
   *  {key: "--theme-secondary-svg-color": value: "blue"}
   * ];
   *
   * //Branding v2
   * var partnerBrand = {
   *  "action-primary": "red",
   *  "action-secondary": "green",
   *  "action-tertiary": "blue"
   * };
   * // result value
   * var brandMap = [
   *  {key: '--theme-action-primary', value: "red"},
   *  {key: '--theme-action-primary-disabled', value: "#ffc2c2"},
   *  {key: '--theme-action-primary-hovered', value: "#d10000"},
   *  {key: '--theme-action-primary-pressed', value: "#8f0000"},
   *  {key: '--theme-action-primary-text', value: '#ffffff'},
   *  {key: "--theme-action-secondary", value: "green"},
   *  {key: '--theme-action-secondary-disabled', value: "#c2ffc2"},
   *  {key: '--theme-action-secondary-hovered', value: "#00d100"},
   *  {key: '--theme-action-secondary-pressed', value: "#008000"},
   *  {key: '--theme-action-secondary-text', value: "green"},
   *  {key: "--theme-action-tertiary", value: "blue"},
   *  {key: '--theme-action-tertiary-disabled', value: "#c2c2ff"},
   *  {key: '--theme-action-tertiary-hovered', value: "#0000d1"},
   *  {key: '--theme-action-tertiary-pressed', value: "#00008f"},
   *  {key: "--theme-action-tertiary-on-dark", value: "???"},
   *  {key: '--theme-action-tertiary-disabled-on-dark', value: "???"},
   *  {key: '--theme-action-tertiary-hovered-on-dark', value: "???"},
   *  {key: '--theme-action-tertiary-pressed-on-dark', value: "???"},
   *  {key: '--theme-interactive-activated', value: "red"},
   *  {key: '--theme-interactive-hovered', value: "#d10000"},
   *  {key: '--theme-interactive-disabled', value: "#ffc2c2"},
   *  {key: '--theme-surface-decorative', value: "blue"},
   *  {key: '--theme-surface-decorative-two', value: "green"},
   *  {key: '--theme-surface-decorative-icon', value: '#ffffff'},
   *  {key: '--theme-surface-decorative-text', value: '#ffffff'},
   *  {key: '--theme-surface-highlight', value: "#e0ffe0"},
   *  {key: '--theme-surface-highlight-text', value: "#008000"},
   *  {key: '--theme-surface-featured', value: "#e0e0ff"},
   *  {key: '--theme-surface-featured-text', value: "???"},
   *  {key: '--theme-surface-section', value: '#ffffff'},
   *  {key: '--theme-border-section', value: "#d5d8de"},
   *  {key: '--theme-border-section-featured', value: "#8585ff"},
   *  {key: '--theme-border-surface-highlight', value: "#85ff85"},
   *  {key: '--theme-brand-icon', value: "#3333ff"},
   *  {key: '--theme-brand-icon-disabled', value: "#c2c2ff"},
   *  {key: '--theme-brand-text', value: "blue"},
   *  {key: '--theme-background', value: "#f8f9fb"}
   * ];
   * @param {any} partnerBrandData Partner branding object from the api request
   * @returns {object[]} Array of objects with the style key and the value of that variable as value
   * @description Branded stylesheet variables are described in open-portal app/app-component.scss file.
   * This function creates the theme wash logic for action-primary, action-secondary and action-tertiary tokens
   * Once the wash logic is done the individual token values are overridden in the brand theme.
   * @todo break out the palette building portion of this function to support both branding variable sets being used side by side.
   */
  mapBrandedStyleVariables(partnerBrandData: any): object[] { // check this return
    let styleVariables: any[] = [];

    if (partnerBrandData) {
      const themeVariables = [
        '--theme-action-primary',
        '--theme-action-primary-disabled',
        '--theme-action-primary-hovered',
        '--theme-action-primary-pressed',
        '--theme-action-primary-text',
        "--theme-action-secondary",
        '--theme-action-secondary-disabled',
        '--theme-action-secondary-hovered',
        '--theme-action-secondary-pressed',
        '--theme-action-secondary-text',
        "--theme-action-tertiary",
        '--theme-action-tertiary-disabled',
        '--theme-action-tertiary-hovered',
        '--theme-action-tertiary-pressed',
        '--theme-action-tertiary-on-dark',
        '--theme-action-tertiary-disabled-on-dark',
        '--theme-action-tertiary-hovered-on-dark',
        '--theme-action-tertiary-pressed-on-dark',
        '--theme-interactive-activated',
        '--theme-interactive-activated-background',
        '--theme-interactive-hovered',
        '--theme-interactive-disabled',
        '--theme-surface-decorative',
        '--theme-surface-decorative-two',
        '--theme-surface-decorative-icon',
        '--theme-surface-decorative-text',
        '--theme-surface-highlight',
        '--theme-surface-highlight-text',
        '--theme-surface-featured',
        '--theme-surface-featured-text',
        '--theme-surface-section',
        '--theme-border-section',
        '--theme-border-section-featured',
        '--theme-border-surface-highlight',
        '--theme-brand-icon',
        '--theme-brand-icon-disabled',
        '--theme-brand-text',
        '--theme-background',
        '--theme-logo-height',
        '--theme-logo-width',
        '--theme-action-primary-border-radius',
        '--theme-action-secondary-border-radius',
        '--theme-action-secondary-border-width',
        '--theme-surface-section-border-radius',
        '--theme-surface-section-border-width',
        '--theme-surface-section-shadow',
        '--theme-surface-featured-border-width',
        '--theme-surface-highlight-border-radius',
        '--theme-surface-highlight-border-width',
        '--theme-tile-hovered-shadow',
        '--theme-tile-border-radius',
        '--theme-tile-border-width',
        '--theme-tile-border-hovered-width',
        '--theme-tile-border-selected-width',
        '--theme-favicon',
        '--theme-logo-small',
        '--theme-logo-email',
        '--theme-logo-large',
        '--theme-logo-height',
        '--theme-logo-width',
        '--theme-title-font',
        '--theme-title-url',
        '--theme-body-font',
        '--theme-body-url',
        '--theme-header-background-colour',
        '--theme-footer-background-colour'
      ];

      // create empty map
      for (let i = 0, j = themeVariables.length; i < j; i++) {
        styleVariables = this.updateStyleVariableMap(styleVariables, themeVariables[i], "");
      }

      // build theme from deprecated partner branding values, primaryColour is required
      if (typeof partnerBrandData.primaryColour !== 'undefined' && partnerBrandData.primaryColour !== '') {
        const deprecatedPrimaryColour = partnerBrandData.primaryColour;
        const deprecatedSecondaryColour = (typeof partnerBrandData.SecondaryButtonColor !== 'undefined')? partnerBrandData.SecondaryButtonColor: "";
        const deprecatedTertiaryColour = (typeof partnerBrandData.hyperlinkColor !== 'undefined')? partnerBrandData.hyperlinkColor: "";
        styleVariables = this.buildThemeColorMap(styleVariables, deprecatedPrimaryColour, deprecatedSecondaryColour, deprecatedTertiaryColour);

        if (typeof partnerBrandData.primaryFont !== 'undefined' && partnerBrandData.primaryFont !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-text', partnerBrandData.primaryFont);
        }
        if (typeof partnerBrandData.selectionColor !== 'undefined' && partnerBrandData.selectionColor !== '') {
          const selectionPalette = this.gamutPaletteService.buildPalette(partnerBrandData.selectionColor);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-activated', partnerBrandData.selectionColor);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-hovered', this.gamutPaletteService.getPaletteColor(selectionPalette, '700'));
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-disabled', this.gamutPaletteService.getPaletteColor(selectionPalette, '100'));
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-activated-background', this.gamutPaletteService.getPaletteColor(selectionPalette, '50'));
        }
        if (typeof partnerBrandData.secondaryColor !== 'undefined' && partnerBrandData.secondaryColor !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative', partnerBrandData.secondaryColour);
        }
        if (typeof partnerBrandData.secondaryFont !== 'undefined' && partnerBrandData.secondaryFont !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-icon', partnerBrandData.secondaryFont);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-text', partnerBrandData.secondaryFont);
        }
        if (typeof partnerBrandData.aiBackgroundColor !== 'undefined' && partnerBrandData.aiBackgroundColor !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight', partnerBrandData.aiBackgroundColor);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured', partnerBrandData.aiBackgroundColor);
        }
        if (typeof partnerBrandData.blockBackgroundColor !== 'undefined' && partnerBrandData.blockBackgroundColor !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-section', partnerBrandData.blockBackgroundColor);
        }
        if (typeof partnerBrandData.aiFontColor !== 'undefined' && partnerBrandData.aiFontColor !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-text', partnerBrandData.aiFontColor);
        }
        if (typeof partnerBrandData.blockBorderColor !== 'undefined' && partnerBrandData.blockBorderColor !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-section', partnerBrandData.blockBorderColor);
        }
        if (typeof partnerBrandData.aiBorderColor !== 'undefined' && partnerBrandData.aiBorderColor !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-surface-highlight', partnerBrandData.aiBorderColor);
        }
        if (typeof partnerBrandData.bodyBackgroundColor !== 'undefined' && partnerBrandData.bodyBackgroundColor !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-background', partnerBrandData.bodyBackgroundColor);
        }

        if (typeof partnerBrandData.primaryButtonRadius !== 'undefined' && partnerBrandData.primaryButtonRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-border-radius', partnerBrandData.primaryButtonRadius);
        }
        if (typeof partnerBrandData.secondaryButtonRadius !== 'undefined' && partnerBrandData.secondaryButtonRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-border-radius', partnerBrandData.secondaryButtonRadius);
        }
        if (typeof partnerBrandData.blockRadius !== 'undefined' && partnerBrandData.blockRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-section-border-radius', partnerBrandData.blockRadius);
        }
        if (typeof partnerBrandData.aiBlockRadius !== 'undefined' && partnerBrandData.aiBlockRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-border-radius', partnerBrandData.aiBlockRadius);
        }
        if (typeof partnerBrandData.aiBorderWidth !== 'undefined' && partnerBrandData.aiBorderWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-border-width', partnerBrandData.aiBorderWidth);
        }

        // external assets
        if (typeof partnerBrandData.favicon !== 'undefined' && partnerBrandData.favicon !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-favicon', partnerBrandData.favicon);
        }
        if (typeof partnerBrandData.logoSmall !== 'undefined' && partnerBrandData.logoSmall !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-small', `url(${ partnerBrandData.logoSmall })`);
        }
        if (typeof partnerBrandData.emailLogo !== 'undefined' && partnerBrandData.emailLogo !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-email', `url(${ partnerBrandData.emailLogo })`);
        }
        if (typeof partnerBrandData.logoLarge !== 'undefined' && partnerBrandData.logoLarge !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-large', `url(${ partnerBrandData.logoLarge })`);
        }
        if (typeof partnerBrandData.headerLogoWidth !== 'undefined' && partnerBrandData.headerLogoWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-width', partnerBrandData.headerLogoWidth);
        }
        if (typeof partnerBrandData.headerLogoHeight !== 'undefined' && partnerBrandData.headerLogoHeight !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-height', partnerBrandData.headerLogoHeight);
        }
        if (typeof partnerBrandData.headerLogoWidth !== 'undefined' && partnerBrandData.headerLogoWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, "--theme-header-logo-width", partnerBrandData.headerLogoWidth);
        }
        if (typeof partnerBrandData.headerLogoHeight !== 'undefined' && partnerBrandData.headerLogoHeight !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, "--theme-header-logo-height", partnerBrandData.headerLogoHeight);
        }
        if (typeof partnerBrandData.headingFontFamily !== 'undefined' && partnerBrandData.headingFontFamily !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-title-font', partnerBrandData.headingFontFamily);
        }
        if (typeof partnerBrandData.headingFontUrl !== 'undefined' && partnerBrandData.headingFontUrl !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-title-url', partnerBrandData.headingFontUrl);
        }
        if (typeof partnerBrandData.bodyFontFamily !== 'undefined' && partnerBrandData.bodyFontFamily !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-body-font', partnerBrandData.bodyFontFamily);
        }
        if (typeof partnerBrandData.bodyFontUrl !== 'undefined' && partnerBrandData.bodyFontUrl !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-body-url', partnerBrandData.bodyFontUrl);
        }
      }

      // build theme from new partner branding variables, action-primary is required
      if (typeof partnerBrandData.actionPrimary !== 'undefined' && partnerBrandData.actionPrimary !== '') {
        styleVariables = this.buildThemeColorMap(styleVariables, partnerBrandData.actionPrimary, partnerBrandData.actionSecondary, partnerBrandData.actionTertiary);

        // set colour overrides
        if (typeof partnerBrandData.actionPrimaryDisabled !== 'undefined' && partnerBrandData.actionPrimaryDisabled !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-disabled', partnerBrandData.actionPrimaryDisabled);
        }
        if (typeof partnerBrandData.actionPrimaryHovered !== 'undefined' && partnerBrandData.actionPrimaryHovered !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-hovered', partnerBrandData.actionPrimaryHovered);
        }
        if (typeof partnerBrandData.actionPrimaryPressed !== 'undefined' && partnerBrandData.actionPrimaryPressed !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-pressed', partnerBrandData.actionPrimaryPressed);
        }
        if (typeof partnerBrandData.actionPrimaryText !== 'undefined' && partnerBrandData.actionPrimaryText !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-text', partnerBrandData.actionPrimaryText);
        }
        if (typeof partnerBrandData.actionSecondaryDisabled !== 'undefined' && partnerBrandData.actionSecondaryDisabled !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-disabled', partnerBrandData.actionSecondaryDisabled);
        }
        if (typeof partnerBrandData.actionSecondaryHovered !== 'undefined' && partnerBrandData.actionSecondaryHovered !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-hovered', partnerBrandData.actionSecondaryHovered);
        }
        if (typeof partnerBrandData.actionSecondaryPressed !== 'undefined' && partnerBrandData.actionSecondaryPressed !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-pressed', partnerBrandData.actionSecondaryPressed);
        }
        if (typeof partnerBrandData.actionSecondaryText !== 'undefined' && partnerBrandData.actionSecondaryText !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-text', partnerBrandData.actionSecondaryText);
        }
        if (typeof partnerBrandData.actionTertiaryDisabled !== 'undefined' && partnerBrandData.actionTertiaryDisabled !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-disabled', partnerBrandData.actionTertiaryDisabled);
        }
        if (typeof partnerBrandData.actionTertiaryHovered !== 'undefined' && partnerBrandData.actionTertiaryHovered !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-hovered', partnerBrandData.actionTertiaryHovered);
        }
        if (typeof partnerBrandData.actionTertiaryPressed !== 'undefined' && partnerBrandData.actionTertiaryPressed !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-pressed', partnerBrandData.actionTertiaryPressed);
        }
        if (typeof partnerBrandData.actionTertiaryOnDark !== 'undefined' && partnerBrandData.actionTertiaryOnDark !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-on-dark', partnerBrandData.actionTertiaryOnDark);
        }
        if (typeof partnerBrandData.actionTertiaryDisabledOnDark !== 'undefined' && partnerBrandData.actionTertiaryDisabledOnDark !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-disabled-on-dark', partnerBrandData.actionTertiaryDisabledOnDark);
        }
        if (typeof partnerBrandData.actionTertiaryHoveredOnDark !== 'undefined' && partnerBrandData.actionTertiaryHoveredOnDark !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-hovered-on-dark', partnerBrandData.actionTertiaryHoveredOnDark);
        }
        if (typeof partnerBrandData.actionTertiaryPressedOnDark !== 'undefined' && partnerBrandData.actionTertiaryPressedOnDark !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-tertiary-pressed-on-dark', partnerBrandData.actionTertiaryPressedOnDark);
        }
        if (typeof partnerBrandData.interactiveActivated !== 'undefined' && partnerBrandData.interactiveActivated !== '') {
          const selectionPalette = this.gamutPaletteService.buildPalette(partnerBrandData.interactiveActivated);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-activated', partnerBrandData.interactiveActivated);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-activated-background', this.gamutPaletteService.getPaletteColor(selectionPalette, '50'));
        }
        if (typeof partnerBrandData.interactiveHovered !== 'undefined' && partnerBrandData.interactiveHovered !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-hovered', partnerBrandData.interactiveHovered);
        }
        if (typeof partnerBrandData.interactiveDisabled !== 'undefined' && partnerBrandData.interactiveDisabled !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-interactive-disabled', partnerBrandData.interactiveDisabled);
        }
        if (typeof partnerBrandData.surfaceDecorative !== 'undefined' && partnerBrandData.surfaceDecorative !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative', partnerBrandData.surfaceDecorative);
        }
        if (typeof partnerBrandData.surfaceDecorativeTwo !== 'undefined' && partnerBrandData.surfaceDecorativeTwo !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-two', partnerBrandData.surfaceDecorativeTwo);
        }
        if (typeof partnerBrandData.surfaceDecorativeIcon !== 'undefined' && partnerBrandData.surfaceDecorativeIcon !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-icon', partnerBrandData.surfaceDecorativeIcon);
        }
        if (typeof partnerBrandData.surfaceDecorativeText !== 'undefined' && partnerBrandData.surfaceDecorativeText !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-decorative-text', partnerBrandData.surfaceDecorativeText);
        }
        if (typeof partnerBrandData.surfaceHighlight !== 'undefined' && partnerBrandData.surfaceHighlight !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight', partnerBrandData.surfaceHighlight);
        }
        if (typeof partnerBrandData.surfaceHighlightText !== 'undefined' && partnerBrandData.surfaceHighlightText !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-text', partnerBrandData.surfaceHighlightText);
        }
        if (typeof partnerBrandData.surfaceFeatured !== 'undefined' && partnerBrandData.surfaceFeatured !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured', partnerBrandData.surfaceFeatured);
        }
        if (typeof partnerBrandData.surfaceFeaturedText !== 'undefined' && partnerBrandData.surfaceFeaturedText !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured-text', partnerBrandData.surfaceFeaturedText);
        }
        if (typeof partnerBrandData.surfaceSection !== 'undefined' && partnerBrandData.surfaceSection !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-section', partnerBrandData.surfaceSection);
        }
        if (typeof partnerBrandData.borderSection !== 'undefined' && partnerBrandData.borderSection !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-section', partnerBrandData.borderSection);
        }
        if (typeof partnerBrandData.borderSectionFeatured !== 'undefined' && partnerBrandData.borderSectionFeatured !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-section-featured', partnerBrandData.borderSectionFeatured);
        }
        if (typeof partnerBrandData.borderSurfaceHighlight !== 'undefined' && partnerBrandData.borderSurfaceHighlight !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-border-surface-highlight', partnerBrandData.borderSurfaceHighlight);
        }
        if (typeof partnerBrandData.brandIcon !== 'undefined' && partnerBrandData.brandIcon !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-icon', partnerBrandData.brandIcon);
        }
        if (typeof partnerBrandData.brandIconDisabled !== 'undefined' && partnerBrandData.brandIconDisabled !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-icon-disabled', partnerBrandData.brandIconDisabled);
        }
        if (typeof partnerBrandData.brandText !== 'undefined' && partnerBrandData.brandText !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-brand-text', partnerBrandData.brandText);
        }
        if (typeof partnerBrandData.background !== 'undefined' && partnerBrandData.background !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-background', partnerBrandData.background);
        }
        if (typeof partnerBrandData.headerBackgroundColour !== 'undefined' && partnerBrandData.headerBackgroundColour !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-header-background-colour', partnerBrandData.headerBackgroundColour);
        }
        if (typeof partnerBrandData.footerBackgroundColour !== 'undefined' && partnerBrandData.footerBackgroundColour !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-footer-background-colour', partnerBrandData.footerBackgroundColour);
        }

        // shape overrides
        if (typeof partnerBrandData.actionPrimaryBorderRadius !== 'undefined' && partnerBrandData.actionPrimaryBorderRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-primary-border-radius', partnerBrandData.actionPrimaryBorderRadius);
        }
        if (typeof partnerBrandData.actionSecondaryBorderRadius !== 'undefined' && partnerBrandData.actionSecondaryBorderRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-border-radius', partnerBrandData.actionSecondaryBorderRadius);
        }
        if (typeof partnerBrandData.actionSecondaryBorderWidth !== 'undefined' && partnerBrandData.actionSecondaryBorderWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-action-secondary-border-width', partnerBrandData.actionSecondaryBorderWidth);
        }
        if (typeof partnerBrandData.surfaceSectionBorderRadius !== 'undefined' && partnerBrandData.surfaceSectionBorderRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-section-border-radius', partnerBrandData.surfaceSectionBorderRadius);
        }
        if (typeof partnerBrandData.surfaceSectionBorderWidth !== 'undefined' && partnerBrandData.surfaceSectionBorderWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-section-border-width', partnerBrandData.surfaceSectionBorderWidth);
        }
        if (typeof partnerBrandData.surfaceSectionShadow !== 'undefined' && partnerBrandData.surfaceSectionShadow !== '') {
          const sectionShadow = this.getBoxShadow(partnerBrandData.surfaceSectionShadow);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-section-shadow', sectionShadow);
        }
        if (typeof partnerBrandData.surfaceFeaturedBorderWidth !== 'undefined' && partnerBrandData.surfaceFeaturedBorderWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-featured-border-width', partnerBrandData.surfaceFeaturedBorderWidth);
        }
        if (typeof partnerBrandData.surfaceHighlightBorderRadius !== 'undefined' && partnerBrandData.surfaceHighlightBorderRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-border-radius', partnerBrandData.surfaceHighlightBorderRadius);
        }
        if (typeof partnerBrandData.surfaceHighlightBorderWidth !== 'undefined' && partnerBrandData.surfaceHighlightBorderWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-surface-highlight-border-width', partnerBrandData.surfaceHighlightBorderWidth);
        }
        if (typeof partnerBrandData.tileBorderRadius !== 'undefined' && partnerBrandData.tileBorderRadius !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-tile-border-radius', partnerBrandData.tileBorderRadius);
        }
        if (typeof partnerBrandData.tileHoveredShadow !== 'undefined' && partnerBrandData.tileHoveredShadow !== '') {
          const tileShadow = this.getBoxShadow(partnerBrandData.tileHoveredShadow);
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-tile-hovered-shadow', tileShadow);
        }
        if (typeof partnerBrandData.tileBorderWidth !== 'undefined' && partnerBrandData.tileBorderWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-tile-border-width', partnerBrandData.tileBorderWidth);
          const tileBorderWidth = this.getTileBorderWidth(partnerBrandData.tileBorderWidth);
          const tileBorderMap = this.getTileBorderMap("", tileBorderWidth);
          styleVariables = this.mergeStyleVariables(styleVariables, tileBorderMap);
        }
        if (typeof partnerBrandData.tileBorderHoveredWidth !== 'undefined' && partnerBrandData.tileBorderHoveredWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-tile-border-hovered-width', partnerBrandData.tileBorderHoveredWidth);
          const tileBorderHoveredWidth = this.getTileBorderWidth(partnerBrandData.tileBorderHoveredWidth);
          const tileBorderHoveredMap = this.getTileBorderMap("hovered", tileBorderHoveredWidth);
          styleVariables = this.mergeStyleVariables(styleVariables, tileBorderHoveredMap);
        }
        if (typeof partnerBrandData.tileBorderSelectedWidth !== 'undefined' && partnerBrandData.tileBorderSelectedWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-tile-border-selected-width', partnerBrandData.tileBorderSelectedWidth);
          const tileBorderSelectedWidth = this.getTileBorderWidth(partnerBrandData.tileBorderSelectedWidth);
          const tileBorderSelectedMap = this.getTileBorderMap('selected', tileBorderSelectedWidth);
          styleVariables = this.mergeStyleVariables(styleVariables, tileBorderSelectedMap);
        }

        // external assets
        if (typeof partnerBrandData.favicon !== 'undefined' && partnerBrandData.favicon !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-favicon', partnerBrandData.favicon);
        }
        if (typeof partnerBrandData.logoSmall !== 'undefined' && partnerBrandData.logoSmall !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-small', `url(${ partnerBrandData.logoSmall })`);
        }
        if (typeof partnerBrandData.logoEmail !== 'undefined' && partnerBrandData.logoEmail !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-email', `url(${ partnerBrandData.logoEmail })`);
        }
        if (typeof partnerBrandData.logoLarge !== 'undefined' && partnerBrandData.logoLarge !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-large', `url(${ partnerBrandData.logoLarge })`);
        }
        if (typeof partnerBrandData.logoHeight !== 'undefined' && partnerBrandData.logoHeight !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-height', partnerBrandData.logoHeight);
        }
        if (typeof partnerBrandData.logoWidth !== 'undefined' && partnerBrandData.logoWidth !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-logo-width', partnerBrandData.logoWidth);
        }
        if (typeof partnerBrandData.titleFont !== 'undefined' && partnerBrandData.titleFont !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-title-font', partnerBrandData.titleFont);
        }
        if (typeof partnerBrandData.titleUrl !== 'undefined' && partnerBrandData.titleUrl !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-title-url', partnerBrandData.titleUrl);
        }
        if (typeof partnerBrandData.bodyFont !== 'undefined' && partnerBrandData.bodyFont !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-body-font', partnerBrandData.bodyFont);
        }
        if (typeof partnerBrandData.bodyUrl !== 'undefined' && partnerBrandData.bodyUrl !== '') {
          styleVariables = this.updateStyleVariableMap(styleVariables, '--theme-body-url', partnerBrandData.bodyUrl);
        }
      }
    }

    // remove blanks
    const cleanStyleVariables = this.cleanBrandStyleVariablesMap(styleVariables);
    return cleanStyleVariables;
  }
}
